import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Sparkles,
  Copy,
  Loader2,
  Twitter,
  Linkedin,
  Instagram,
  FileText,
  Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`
});

export function AIMetaGenerator({ eventId, currentMeta, onGenerated }) {
  const [loading, setLoading] = useState(false);
  const [generatedMeta, setGeneratedMeta] = useState('');

  const generate = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/generate-meta`,
        { eventId },
        { headers: getAuthHeaders() }
      );
      setGeneratedMeta(response.data.metaDescription);
      toast.success('Meta description generated!');
    } catch (error) {
      toast.error('Failed to generate meta description');
    } finally {
      setLoading(false);
    }
  };

  const useGenerated = () => {
    if (generatedMeta && onGenerated) {
      onGenerated(generatedMeta);
      toast.success('Meta description applied!');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={generate}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
          )}
          AI Generate
        </Button>
        {generatedMeta && (
          <Badge variant="secondary" className="text-xs">
            {generatedMeta.length} chars
          </Badge>
        )}
      </div>
      
      {generatedMeta && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
          <p className="text-sm text-gray-700 mb-2">{generatedMeta}</p>
          <Button
            type="button"
            size="sm"
            onClick={useGenerated}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Check className="w-4 h-4 mr-1" />
            Use This
          </Button>
        </div>
      )}
    </div>
  );
}

export function AISocialPostGenerator({ eventId, eventName }) {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState('linkedin');
  const [loading, setLoading] = useState(false);
  const [generatedPost, setGeneratedPost] = useState('');
  const [copied, setCopied] = useState(false);

  const platformIcons = {
    linkedin: Linkedin,
    twitter: Twitter,
    instagram: Instagram
  };

  const generate = async () => {
    try {
      setLoading(true);
      setGeneratedPost('');
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/generate-social-post`,
        { eventId, platform },
        { headers: getAuthHeaders() }
      );
      setGeneratedPost(response.data.post);
      toast.success(`${platform} post generated!`);
    } catch (error) {
      toast.error('Failed to generate post');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedPost);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const PlatformIcon = platformIcons[platform];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
          Generate Social Post
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
            AI Social Post Generator
          </DialogTitle>
          <DialogDescription>
            Generate a promotional post for "{eventName}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center gap-3">
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="linkedin">
                  <div className="flex items-center">
                    <Linkedin className="w-4 h-4 mr-2 text-blue-600" />
                    LinkedIn
                  </div>
                </SelectItem>
                <SelectItem value="twitter">
                  <div className="flex items-center">
                    <Twitter className="w-4 h-4 mr-2 text-sky-500" />
                    Twitter/X
                  </div>
                </SelectItem>
                <SelectItem value="instagram">
                  <div className="flex items-center">
                    <Instagram className="w-4 h-4 mr-2 text-pink-600" />
                    Instagram
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={generate} disabled={loading}>
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Generate
            </Button>
          </div>

          {generatedPost && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PlatformIcon className="w-5 h-5 text-gray-600" />
                  <span className="font-medium capitalize">{platform} Post</span>
                </div>
                <Badge variant="secondary">{generatedPost.length} chars</Badge>
              </div>
              
              <Textarea
                value={generatedPost}
                readOnly
                rows={8}
                className="font-mono text-sm"
              />
              
              <Button onClick={copyToClipboard} className="w-full">
                {copied ? (
                  <Check className="w-4 h-4 mr-2" />
                ) : (
                  <Copy className="w-4 h-4 mr-2" />
                )}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AIToolsCard({ eventId, eventName }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center">
          <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
          AI Tools
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <AISocialPostGenerator eventId={eventId} eventName={eventName} />
        
        <p className="text-xs text-gray-500">
          Generate social media posts, meta descriptions, and more using AI.
        </p>
      </CardContent>
    </Card>
  );
}

export default {
  AIMetaGenerator,
  AISocialPostGenerator,
  AIToolsCard
};
